import {
  isArdoqError,
  isSuccessResponse,
  toArdoqError,
  type Result,
} from '@ardoq/common-helpers';
import { isArray } from 'lodash';
import {
  TableFilterExpression,
  tableFilterOperatorLookupTable,
  VALUE_PRESENCE_OPERATORS,
} from './types';

export type URLTableFilterExpression = Pick<
  TableFilterExpression,
  'id' | 'filterId' | 'operator' | 'selection'
>;

// Separators for URL encoding
const FILTER_SEPARATOR = ',';
const COMPONENT_SEPARATOR = ';';
const SELECTION_SEPARATOR = '|';

const operatorFromString = (expr: URLTableFilterExpression): string =>
  expr.operator;
const operatorToString = (
  str: string
): Result<URLTableFilterExpression['operator']> => {
  const allSupportedOperators: string[] = [
    ...Object.values(tableFilterOperatorLookupTable).flat(),
    ...VALUE_PRESENCE_OPERATORS,
  ];

  if (allSupportedOperators.includes(str)) {
    return str as URLTableFilterExpression['operator'];
  }

  return toArdoqError({
    error: new Error(`Unsupported filter operator: ${str}`),
  });
};

const filterIdFromString = (expr: URLTableFilterExpression): string =>
  expr.filterId;
const filterIdToString = (str: string): string => str;

const selectionToString = (expr: URLTableFilterExpression): string => {
  if (expr.selection === undefined) {
    return '';
  }

  if (isArray(expr.selection)) {
    return joinAndEscape(expr.selection, SELECTION_SEPARATOR);
  }

  return joinAndEscape([expr.selection], SELECTION_SEPARATOR);
};

const selectionFromString = (
  str: string
): URLTableFilterExpression['selection'] => {
  if (str === '') {
    return undefined;
  }
  return splitAndUnescape(str, SELECTION_SEPARATOR);
};

const tableFilterExpressionToString = (
  expr: URLTableFilterExpression
): string =>
  joinAndEscape(
    [
      filterIdFromString(expr),
      operatorFromString(expr),
      selectionToString(expr),
      ...(expr.id === undefined ? [] : [expr.id]),
    ],
    COMPONENT_SEPARATOR
  );

export const tableFilterExpressionsToString = (
  expressions: URLTableFilterExpression[]
): string =>
  joinAndEscape(
    expressions.map(tableFilterExpressionToString),
    FILTER_SEPARATOR
  );

const tableFilterExpressionFromString = (
  value: string
): Result<URLTableFilterExpression> => {
  const parts = splitAndUnescape(value, COMPONENT_SEPARATOR);
  if (parts.length < 3) {
    return toArdoqError({
      error: new Error(`Malformed filter segment: ${value}`),
    });
  }

  const [filterIdStr, operatorStr, selectionStr, idStr] = parts;
  const operatorResult = operatorToString(operatorStr);
  if (isArdoqError(operatorResult)) {
    return operatorResult;
  }

  return {
    id: idStr || undefined,
    filterId: filterIdToString(filterIdStr),
    operator: operatorResult,
    selection: selectionFromString(selectionStr),
  };
};

export const tableFilterExpressionsFromString = (
  value: string
): URLTableFilterExpression[] =>
  splitAndUnescape(value, FILTER_SEPARATOR)
    .map(tableFilterExpressionFromString)
    .filter(isSuccessResponse);

type IdentifiedExpression = URLTableFilterExpression & { id: string };

/**
 * Gives every expression a unique id. The first occurrence of an id is kept;
 * missing and repeated ids are replaced with ones from `createId`.
 */
export const backfillExpressionIds = (
  expressions: URLTableFilterExpression[],
  createId: () => string
): IdentifiedExpression[] => {
  const usedIds = new Set<string>();

  const createUnusedId = (): string => {
    const id = createId();
    return usedIds.has(id) ? createUnusedId() : id;
  };

  return expressions.map(expression => {
    const id =
      expression.id && !usedIds.has(expression.id)
        ? expression.id
        : createUnusedId();
    usedIds.add(id);
    return { ...expression, id };
  });
};

/**
 * Deterministic generator for URLs written before ids existed, so the same
 * URL always decodes to the same ids.
 */
export const createLegacyExpressionIdGenerator = (): (() => string) => {
  let next = 0;
  return () => `legacy-${next++}`;
};

const ESCAPE = '\\';

/**
 * Escapes backslashes and separators in each string and joins them with the
 * separator.
 *
 * Example: joinAndEscape(["a|b", "c\\"], "|") => "a\|b|c\\"
 */
const joinAndEscape = (values: string[], separator: string): string =>
  values
    .map(value =>
      value
        .replaceAll(ESCAPE, `${ESCAPE}${ESCAPE}`)
        .replaceAll(separator, `${ESCAPE}${separator}`)
    )
    .join(separator);

/**
 * Split on the separator, but ignore escaped separators. A backslash that
 * escapes neither a backslash nor the separator is kept, so URLs written
 * before backslashes were escaped still decode.
 *
 * Example: splitAndUnescape("a\|b|c\\", "|") => ["a|b", "c\"]
 */
const splitAndUnescape = (str: string, separator: string): string[] => {
  const result: string[] = [];
  let current = '';
  let i = 0;

  while (i < str.length) {
    if (
      str[i] === ESCAPE &&
      (str[i + 1] === separator || str[i + 1] === ESCAPE)
    ) {
      current += str[i + 1];
      i += 2;
    } else if (str[i] === separator) {
      result.push(current);
      current = '';
      i += 1;
    } else {
      current += str[i];
      i += 1;
    }
  }

  result.push(current);
  return result;
};
